/* eslint-disable react-hooks/exhaustive-deps */
import React, {useCallback, useEffect} from "react"
import {useTranslation} from "react-i18next"
import {useHistory} from "react-router-dom"
import {Switch} from "antd"
import {KlassappTableHeader, KlassappTable, BaseNewFilter} from "uiKit"
import {KlassappTableHOC} from "HOC"
import {useFilterStorage, useModel} from "hooks"
import {handleError, truncateWithEllipses, checkPermission, handleErrorMessage} from "helpers"
import {settingEmailTemplatesService} from "services"
import {Permissions} from "types/permission"
import styles from "../SMSTemplates/SMSTemplates.module.css"
import {SettingTemplatesTabs} from "../../Templates"
import {FilterKey} from "types/filter"

function DefaultTemplates(props) {
    const {
        dispatch,
        dispatchFunc,
        page,
        total,
        pageSize,
        columns,
        data,
        allFields,
        fields,
        tableHeaderActions,
        orderField,
        isLoading,
        isShowTableHeaderAction,
        isLoadedTableFuncs,
        getCurrentData,
        getCurrentPage,
        getCurrentPageSize,
        onClickShowConfirmModal
    } = props
    const {t} = useTranslation(["templates", "settings", "common"])
    const history = useHistory()
    const model = useModel()
    const {searchValue, currentFilter, changeSearch, clearFilter, applyFilter} = useFilterStorage(
        FilterKey.DefaultTemplatesSettings,
        {}
    )

    const canEdit = checkPermission({staff: [Permissions.Staff.Settings.SystemSettings.EmailTemplates.Edit]}, model)

    const getFields = () => {
        return [
            t("templates:code"),
            t("templates:name"),
            t("templates:description"),
            t("templates:isActive"),
            t("templates:lastEdited")
        ]
    }

    const getPageTitle = () => {
        return t("templates:titleTemplate", {field: "Default"})
    }

    const getFilterMemoryKey = () => {
        return FilterKey.DefaultTemplatesSettings
    }

    const getParams = () => {
        const storageData = model.getStorageFilter(FilterKey.DefaultTemplatesSettings)
        const page = storageData?.page || getCurrentPage()
        const pageSize = storageData?.pageSize || getCurrentPageSize()
        let orderFieldData = orderField
        if (storageData?.colOrd) {
            const columns: any[] = getColumns()
            const columnOrder = columns.find((column: any) => column.columnIndex === storageData.colOrd.idx)
            orderFieldData = {field: columnOrder?.orderField || columnOrder?.field, order: storageData.colOrd.order}
        }
        const params: any = {
            range: {page, pageSize},
            filter: {
                templateType: SettingTemplatesTabs.Default,
                search: searchValue
            },
            linkedObjects: true
        }
        if (orderFieldData?.field && orderFieldData?.order) {
            params.sort = {
                orderBy: orderFieldData.field,
                orderDir: orderFieldData.order
            }
        }
        return params
    }

    const getData = async () => {
        dispatch({isLoading: true})
        try {
            const params = getParams()
            const {data, total} = await settingEmailTemplatesService.list(params)
            const newData = data.map((el) => ({
                ...el,
                id: el.emailTemplateId,
                description: truncateWithEllipses(el.description ?? "", 35, 35)
            }))
            dispatch({data: newData, total})
        } catch (error) {
            handleError(error)
        } finally {
            dispatch({isLoading: false})
        }
    }

    const onToggleIsActive = async (row, isActive: boolean) => {
        if (!canEdit) {
            return handleErrorMessage(t("templates:dontHavePermissionToEdit"))
        }
        try {
            dispatch({isLoading: true})
            await settingEmailTemplatesService.activateDefaultTemplate(row.emailTemplateId, isActive)
            await getData()
        } catch (error) {
            handleError(error)
        } finally {
            dispatch({isLoading: false})
        }
    }

    const getColumns = () => {
        return [
            {
                title: t("templates:code"),
                field: "code",
                sortable: true
            },
            {
                title: t("templates:name"),
                field: "title",
                sortable: true
            },
            {
                title: t("templates:description"),
                field: "description",
                sortable: true
            },
            {
                title: t("templates:isActive"),
                field: "isActive",
                style: {minWidth: 100},
                render: (_value, row) => (
                    <div onClick={(e) => e.stopPropagation()}>
                        <Switch
                            checked={!!row.isActive}
                            disabled={!canEdit}
                            onChange={(checked) => onToggleIsActive(row, checked)}
                        />
                    </div>
                )
            },
            {
                title: t("templates:lastEdited"),
                field: "updatedAt",
                fieldType: "date",
                format: "MM/DD/YYYY",
                style: {minWidth: 150},
                sortable: true
            }
        ]
    }

    const onClickRowItem = (row) => {
        history.push(`/settings/templates/default/detail?id=${row.id}`)
    }

    const onClickDeleteMulti = async () => {
        if (!checkPermission({staff: [Permissions.Staff.Settings.SystemSettings.EmailTemplates.Delete]}, model)) {
            return handleErrorMessage(t("templates:dontHavePermissionToDelete"))
        }

        const data = getCurrentData()
        const checkedItems = data.filter((item) => item.isChecked)
        if (checkedItems.some((item) => item.isDefault)) {
            return handleErrorMessage(t("templates:cannotDeleteDefault"))
        }

        const checkedIds = checkedItems.map((item) => item.id)
        try {
            dispatch({isLoading: true, isShowTableHeaderAction: false, isHideMenuActions: false})
            await settingEmailTemplatesService.delete(checkedIds)
            await getData()
        } catch (error) {
            handleError(error)
        } finally {
            dispatch({isLoading: false})
        }
    }

    const onClickDuplicateMulti = async () => {
        if (!checkPermission({staff: [Permissions.Staff.Settings.SystemSettings.EmailTemplates.Add]}, model)) {
            return handleErrorMessage(t("templates:dontHavePermissionToCreate"))
        }

        const data = getCurrentData()
        const checkedItems = data.filter((item) => item.isChecked)
        if (checkedItems.some((item) => !item.isDefault)) {
            return handleErrorMessage(t("templates:duplicateOnlyDefault"))
        }

        const payload = checkedItems.map(({title, description, template, templateType, slug, editorType}) => ({
            title: `${title} (Copy)`,
            description,
            template,
            templateType,
            slug,
            editorType
        }))
        try {
            dispatch({isLoading: true, isShowTableHeaderAction: false, isHideMenuActions: false})
            await settingEmailTemplatesService.create(payload)
            await getData()
        } catch (error) {
            handleError(error)
        } finally {
            dispatch({isLoading: false})
        }
    }

    const getTableHeaderActions = useCallback(
        (isShowDuplicateBtn = true, checkedData = []) => {
            const actions = []
            const canDelete =
                checkedData.length > 0 &&
                checkedData.every((item) => !item.isDefault) &&
                checkPermission({staff: [Permissions.Staff.Settings.SystemSettings.EmailTemplates.Delete]}, model)
            const canDuplicate =
                isShowDuplicateBtn &&
                checkedData.length === 1 &&
                checkedData[0]?.isDefault &&
                checkPermission({staff: [Permissions.Staff.Settings.SystemSettings.EmailTemplates.Add]}, model)

            if (canDelete) {
                actions.push({
                    title: t("common:action.delete"),
                    icon: "DELETE",
                    action: () => onClickShowConfirmModal("DELETE")
                })
            }
            if (canDuplicate) {
                actions.push({
                    title: t("common:action.duplicate"),
                    icon: "DUPLICATE",
                    action: () => onClickDuplicateMulti()
                })
            }
            return actions
        },
        [model, onClickShowConfirmModal, t]
    )

    const onClickEdit = (editedItem) => {
        onClickRowItem(editedItem)
    }

    const onClickDelete = async (deletedItem) => {
        if (!checkPermission({staff: [Permissions.Staff.Settings.SystemSettings.EmailTemplates.Delete]}, model)) {
            return handleErrorMessage(t("templates:dontHavePermissionToDelete"))
        }

        if (deletedItem?.isDefault) {
            return handleErrorMessage(t("templates:cannotDeleteDefault"))
        }

        try {
            dispatch({isLoading: true})
            await settingEmailTemplatesService.delete([deletedItem.id])
            await getData()
        } catch (error) {
            handleError(error)
        } finally {
            dispatch({isLoading: false})
        }
    }

    useEffect(() => {
        dispatch({isClassComponent: false})
        dispatchFunc([
            {key: "getPageTitle", func: getPageTitle},
            {key: "getListData", func: getData},
            {key: "getFilterMemoryKey", func: getFilterMemoryKey},
            {key: "getFields", func: getFields},
            {key: "getColumns", func: getColumns},
            {key: "onClickDeleteMulti", func: onClickDeleteMulti},
            {key: "onClickDuplicateMulti", func: onClickDuplicateMulti},
            {key: "onClickEdit", func: onClickEdit},
            {key: "onClickDelete", func: onClickDelete},
            {key: "onClickRowItem", func: onClickRowItem},
            {key: "getTableHeaderActions", func: getTableHeaderActions}
        ])
    }, [])

    useEffect(() => {
        if (isLoadedTableFuncs) {
            getData()
        }
    }, [isLoadedTableFuncs, page, pageSize, orderField?.field, orderField?.order, searchValue])

    useEffect(() => {
        props.updateTableHeaderActions()
    }, [data])

    const onClickApplyFilter = () => {
        dispatch({page: 1})
        applyFilter()
        getData()
    }

    const onClickClearFilter = () => {
        clearFilter()
    }

    const onChangeSearch = (searchValue) => {
        dispatch({page: 1})
        changeSearch(searchValue)
    }

    return (
        <>
            <BaseNewFilter
                className={styles.filterWrap}
                searchValue={searchValue}
                filter={currentFilter}
                onClick={onClickApplyFilter}
                onClickClear={onClickClearFilter}
                onSearchInput={onChangeSearch}
            />
            <KlassappTableHeader
                isShowAction={isShowTableHeaderAction}
                actions={tableHeaderActions}
                page={page}
                total={total}
                defaultPageSize={pageSize}
                onChangePage={props.onChangePage}
                onChangeRowPerPage={props.onChangeRowPerPage}
                fields={fields}
                allFields={allFields}
                onChangeFields={props.onChangeFields}
                onChangeAllFields={props.onChangeAllFields}
                onDraggableColumn={props.onDraggableColumn}
            />
            <KlassappTable
                columns={columns}
                fields={fields}
                data={data}
                menuActions={[]}
                isLoading={isLoading}
                allFields={allFields}
                isShowCheckedColumn
                orderField={orderField}
                onClickRowItem={onClickRowItem}
                onChangeFields={props.onChangeFields}
                onUpdateRowData={props.onUpdateRowData}
                onUpdateTableData={props.onUpdateTableData}
                onClickSortColumn={props.onClickSortColumn}
                onDraggableColumn={props.onDraggableColumn}
                onChangeAllFields={props.onChangeAllFields}
            />
        </>
    )
}

export default KlassappTableHOC(DefaultTemplates)
